import React, { useEffect } from 'react';
import telemetry from '../services/telemetry.service';
import './styles/FlowPreviewModal.css';

export default function FlowPreviewModal({ flow, siteCollectionId, onClose }) {
  if (!flow) return null;

  // Fire the capability metric event once the modal is visibly mounted.
  // Per the Telemetry Guide §6.1: fire after the preview is confirmed open,
  // not on the button click. useEffect runs after the DOM is committed (post-paint).
  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    const flowId = flow?.id;
    if (flowId && siteCollectionId) {
      telemetry.track('flow_preview.opened', {
        classification: 'Informational',
        dimensions: {
          siteCollectionId,
          flowId,
        },
      });
    }
  // Stable deps: re-fires only if a different flow or site is opened in the modal
  }, [flow?.id, siteCollectionId]); // eslint-disable-line react-hooks/exhaustive-deps

  const steps = Array.isArray(flow.steps) ? flow.steps : [];

  const getStepLabel = (step, index) => {
    const order = step?.stepOrder ?? index + 1;
    const name = step?.stepName || 'Untitled Step';
    return `Step ${order}: ${name}`;
  };

  const getExperienceName = (step) => step?.uxExperience?.name || 'N/A';
  const getExperienceType = (step) => step?.uxExperience?.uxExperienceType || 'N/A';
  const getExperienceLocation = (step) => step?.uxExperience?.elementLocation || 'N/A';

  const getQuestions = (step) => {
    const questions = step?.uxExperience?.content?.questions;
    return Array.isArray(questions) ? questions : [];
  };

  return (
    <div id="flow-preview-modal" className="modal-overlay" role="dialog" aria-modal="true" aria-labelledby="flow-preview-title">
      <div className="modal-content">
        <h2 id="flow-preview-title">{flow.flowName || 'Flow Preview'}</h2>
        <p><strong>Description:</strong> {flow.flowDescription || 'No flow description provided.'}</p>
        <p><strong>Status:</strong> {flow.status || 'Unknown'}</p>

        {steps.length === 0 ? (
          <div className="step-section">
            <h3>No previewable steps available</h3>
            <p>This flow does not currently expose step metadata in the preview response.</p>
          </div>
        ) : (
          steps.map((step, index) => (
          <div key={index} className="step-section">
            <h3>{getStepLabel(step, index)}</h3>
            <p><strong>UX Experience:</strong> {getExperienceName(step)}</p>
            <p><strong>Type:</strong> {getExperienceType(step)}</p>
            <p><strong>Location:</strong> {getExperienceLocation(step)}</p>

            {step?.uxExperience?.content ? (
              <div className="ux-preview-box">
                <h4>{step.uxExperience.content.header || 'Experience Content'}</h4>
                <p>{step.uxExperience.content.body || 'No content body provided.'}</p>

                <form className="ux-form-preview">
                  {getQuestions(step).map((q, i) => (
                    <div key={i} className="question-block">
                      <label><strong>Q{i + 1}: {q.question || 'Untitled question'}</strong></label>

                      {(q.type === 'SingleChoice' || q.type === 'SingleSelect') && (
                        <div className="options">
                          {q.options?.map((opt, j) => (
                            <label key={j}>
                              <input type="radio" name={`step-${index}-q-${i}`} disabled /> {opt}
                            </label>
                          ))}
                        </div>
                      )}

                      {q.type === 'MultiChoice' && (
                        <div className="options">
                          {q.options?.map((opt, j) => (
                            <label key={j}>
                              <input type="checkbox" name={`step-${index}-q-${i}-${j}`} disabled /> {opt}
                            </label>
                          ))}
                        </div>
                      )}

                      {q.type === 'TextMulti' && (
                        <>
                          {Array.isArray(q.options) && q.options.length > 0 && (
                            <div className="options">
                              {q.options.map((opt, j) => (
                                <label key={j}>
                                  <input type="checkbox" name={`step-${index}-q-${i}-textmulti-${j}`} disabled /> {opt}
                                </label>
                              ))}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  ))}

                  {getQuestions(step).length === 0 && (
                    <p>No interactive questions for this step.</p>
                  )}
                </form>
              </div>
            ) : (
              <div className="ux-preview-box">
                <p>No UX content payload available for this step.</p>
              </div>
            )}
          </div>
          ))
        )}

        <button type="button" className="close-btn" onClick={onClose}>Close</button>
      </div>
    </div>
  );
}